import { FC } from "react";
import { useData } from "data/data-provider";

export interface ContactSectionProps {
  className?: string;
}

/**
 * Design B — Contact page hero block.
 *
 * Sits over the page banner image and renders the contact-page heading +
 * subtitle. Every piece of copy is sourced from FMS data:
 *  - Eyebrow pill: `banner_tagline` (falls back to company name).
 *  - Heading: a localized "Talk to {company}" using `company_name`, with a
 *    sensible default when the tenant hasn't filled in a name yet.
 *  - Subtitle: `seo_description` (falls back to a neutral default).
 *
 * No more hardcoded "Home > Contact Us" breadcrumb pill or "GET IN TOUCH"
 * yellow gradient — both clashed with the active palette and weren't
 * editable from the FMS.
 */
const ContactSection: FC<ContactSectionProps> = ({ className = "" }) => {
  const { companyDetails } = useData();

  const tagline: string =
    companyDetails?.banner_tagline?.trim() ||
    companyDetails?.company_name?.trim() ||
    "";

  const companyName: string =
    companyDetails?.company_name?.trim() ||
    companyDetails?.company?.name?.trim() ||
    "";

  const heading = companyName ? `Talk to ${companyName}` : "Get in touch";

  const subtitle: string =
    companyDetails?.seo_description?.trim() ||
    "Have questions or ready to book? Send us a note and the team will reply within one business day.";

  return (
    <div
      className={`nc-SectionHero relative ${className}`}
      data-nc-id="SectionHero"
    >
      {/* Plain `container` — match the rest of the home page sections + the
          Header logo. Earlier this added `px-4 sm:px-8 lg:px-12` on top of
          the container, which pushed the contact heading further inset than
          the logo / contact body below. */}
      <div className="container py-12 sm:py-16 lg:py-20 relative">
        {tagline && (
          <div className="mb-6 sm:mb-8">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#FFFFFF26] border border-primary-100 px-4 py-2 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-banner-description-color backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-primary-200" />
              {tagline}
            </span>
          </div>
        )}

        <div className="max-w-3xl">
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-bold text-banner-heading-color leading-[1.05] tracking-tight">
            {heading}
          </h1>

          <div className="mt-5 sm:mt-6 lg:mt-7">
            <p className="text-base sm:text-lg md:text-xl !text-banner-description-color font-medium leading-relaxed max-w-2xl">
              {subtitle}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSection;
